import { CareEnvironment } from '../../';
import {
  familyInvolvementPreferenceNone,
  familyInvolvementPreferenceMinimal,
  familyInvolvementPreferenceBalance,
  familyInvolvementPreferenceHeavily,
} from '../careEnvironments/calcCareEnvironmentsDerivatives';

export type InsightRule = (client: Client) => ClientInsight | null;

// Nursing home (fullCareFacility) monthly rate thresholds (2021 base × 1.2495 inflation to 2025)
// Derived from 75th/25th percentiles of 13,270 zip codes in ZipCareCosts
const HCOL_RATE_THRESHOLD = 14024; // 75th percentile inflated ($11,224 base × 1.2495)
const LCOL_RATE_THRESHOLD = 10471; // 25th percentile inflated ($8,380 base × 1.2495)

const LONG_HORIZON_YEARS = 20;
const SHORT_HORIZON_YEARS = 10;

const LOW_AGE_THRESHOLD = 50;

function getNursingHomeRate(client: Client): number | null {
  const costs = client.clientLocalCareEnvironmentCosts;
  if (!costs) return null;
  const fc = costs[CareEnvironment.fullCareFacility];
  if (!fc || !fc.rateAmount) return null;
  return fc.rateAmount;
}

function getFamilyInvolvementPreference(client: Client): string {
  return (client.intakeSurvey?.clientFamilyInvolvementPreference ?? '').toLowerCase();
}

function getFamilyCarePercent(client: Client): number {
  return Math.round((client.allPhaseCosts?.allPhaseFamilyProvidedPercent ?? 0) * 100);
}

export const costOfLivingRule: InsightRule = (client) => {
  const costs = client.clientLocalCareEnvironmentCosts;
  const rate = getNursingHomeRate(client);
  if (rate === null) return null;

  const fc = costs[CareEnvironment.fullCareFacility];
  const localeType = fc.rateLocaleType;
  const zipCode = fc.zipCode || '';

  const areaLabel =
    localeType === 'Local' && zipCode
      ? `Your area (${zipCode})`
      : localeType === 'State'
        ? 'Your state'
        : 'Your region';

  const advisorAreaLabel =
    localeType === 'Local' && zipCode
      ? `Their zip code, ${zipCode},`
      : localeType === 'State'
        ? 'Their state'
        : 'Their region';

  if (rate >= HCOL_RATE_THRESHOLD) {
    return {
      id: 'cost-of-living',
      category: 'cost-driver',
      categoryLabel: 'Cost of Care',
      locations: ['summary', 'cost-factors'],
      title: `${areaLabel} has above-average care costs.`,
      titleHighlights: ['above-average'],
      description: 'Providers in high cost-of-living regions charge premium rates, which is reflected in your projections.',
      summaryDescription: 'Higher regional rates are factored into your projections.',
      message: `${advisorAreaLabel} is in a high cost of living area, contributing to higher LTC costs.`,
      sentiment: 'caution',
    };
  }

  if (rate <= LCOL_RATE_THRESHOLD) {
    return {
      id: 'cost-of-living',
      category: 'cost-driver',
      categoryLabel: 'Cost of Care',
      locations: ['summary', 'cost-factors'],
      title: `${areaLabel} has below-average care costs.`,
      titleHighlights: ['below-average'],
      description: 'Lower regional costs help keep projected care expenses down compared to national averages.',
      summaryDescription: 'Lower regional rates help reduce your projected costs.',
      message: `${advisorAreaLabel} is in a lower cost of living area, helping to keep LTC costs down.`,
      sentiment: 'positive',
    };
  }

  return {
    id: 'cost-of-living',
    category: 'cost-driver',
    categoryLabel: 'Cost of Care',
    locations: ['summary', 'cost-factors'],
    title: `${areaLabel} has typical care costs.`,
    titleHighlights: ['typical'],
    description: 'Care costs in your area are near the national average, which is reflected in your projections.',
    summaryDescription: 'Average regional rates are factored into your projections.',
    message: `${advisorAreaLabel} has care costs near the national average.`,
    sentiment: 'neutral',
  };
};

export const inflationHorizonRule: InsightRule = (client) => {
  const yearsTillLtc = client.appliedInferenceSet?.yearsTillLtc;
  if (yearsTillLtc == null) return null;

  if (yearsTillLtc >= LONG_HORIZON_YEARS) {
    return {
      id: 'inflation-horizon',
      category: 'cost-driver',
      categoryLabel: 'Inflation',
      locations: ['summary', 'cost-factors'],
      title: `With a ${yearsTillLtc}-year time horizon, healthcare inflation has decades to compound.`,
      titleHighlights: [`${yearsTillLtc}-year time horizon`],
      description: 'The same long horizon can work in your favor through compound investment growth or inflation-protected insurance.',
      summaryDescription: 'Time can also work in your favor through investment growth.',
      message: `They're predicted to need LTC a long time from now, so future healthcare inflation can compound over many years and increase costs significantly. Keep in mind that this same time horizon can apply to compound investment returns or insurance inflation protection to help keep up with healthcare inflation.`,
      sentiment: 'caution',
    };
  }

  if (yearsTillLtc <= SHORT_HORIZON_YEARS) {
    return {
      id: 'inflation-horizon',
      category: 'cost-driver',
      categoryLabel: 'Inflation',
      locations: ['summary', 'cost-factors'],
      title: `With a ${yearsTillLtc}-year time horizon, inflation has limited time to compound.`,
      titleHighlights: [`${yearsTillLtc}-year time horizon`],
      description: 'A shorter horizon means healthcare costs are closer to today\'s rates, making projections more predictable.',
      summaryDescription: 'Costs stay closer to today\'s rates.',
      message: `They're predicted to need LTC relatively soon, so healthcare inflation has less time to compound and will have a smaller impact on total costs.`,
      sentiment: 'positive',
    };
  }

  return {
    id: 'inflation-horizon',
    category: 'cost-driver',
    categoryLabel: 'Inflation',
    locations: ['summary', 'cost-factors'],
    title: `With a ${yearsTillLtc}-year time horizon, healthcare inflation will have a moderate impact.`,
    titleHighlights: [`${yearsTillLtc}-year time horizon`],
    description: 'The same long horizon can work in your favor through compound investment growth or inflation-protected insurance.',
    summaryDescription: 'Time can also work in your favor through investment growth.',
    message: `They're predicted to need LTC in about ${yearsTillLtc} years, so healthcare inflation will have a moderate impact on total costs.`,
    sentiment: 'neutral',
  };
};

export const likelihoodRule: InsightRule = (client) => {
  const age = client.intakeSurvey?.clientAge;
  if (age == null) return null;

  if (age < LOW_AGE_THRESHOLD) {
    return {
      id: 'likelihood',
      category: 'likelihood',
      categoryLabel: 'Age & Likelihood',
      locations: ['summary', 'predictions'],
      title: `At ${age}, your current LTC likelihood appears low — but it will rise.`,
      titleHighlights: [`At ${age}`],
      description: 'As you age, your likelihood of needing long-term care is expected to increase substantially.',
      summaryDescription: 'Your likelihood is expected to increase with age.',
      message: `They are only ${age} years old, and as a result may pass away before they reach an age where LTC becomes likely, so their likelihood today may look diminished. As they age, it is expected that their LTC likelihood will increase.`,
      sentiment: 'neutral',
    };
  }

  return {
    id: 'likelihood',
    category: 'likelihood',
    categoryLabel: 'Age & Likelihood',
    locations: ['summary', 'predictions'],
    title: `At ${age}, your LTC likelihood is typical — and it will continue to rise with age.`,
    titleHighlights: [`At ${age}`],
    description: 'Most people will need some form of long-term care as they age. Planning ahead can make a significant difference.',
    summaryDescription: 'Planning ahead can make a significant difference.',
    message: `At ${age}, their likelihood of needing LTC is in the typical range. As they age, this likelihood will continue to increase.`,
    sentiment: 'neutral',
  };
};

export const familyCaregivingRule: InsightRule = (client) => {
  const pref = getFamilyInvolvementPreference(client);
  const familyPercent = getFamilyCarePercent(client);
  const clientFirstName = client.clientFirstName ?? '';

  const isHigh = pref.includes(familyInvolvementPreferenceHeavily) ||
    pref.includes(familyInvolvementPreferenceBalance);

  const isLow = pref.includes(familyInvolvementPreferenceNone) ||
    pref.includes(familyInvolvementPreferenceMinimal);

  if (isHigh) {
    const title = familyPercent > 0
      ? `Family is expected to cover ${familyPercent}% of ${clientFirstName}'s caregiving needs.`
      : `Family is expected to play a significant role in ${clientFirstName}'s caregiving.`;
    return {
      id: 'family-caregiving',
      category: 'care-structure',
      categoryLabel: 'Family Caregiving',
      locations: ['summary', 'care-phases', 'cost-factors'],
      title,
      titleHighlights: familyPercent > 0 ? [`${familyPercent}%`] : ['significant role'],
      description: 'This reduces projected costs but assumes consistent, long-term availability — worth confirming before relying on it.',
      summaryDescription: 'This reduces costs but depends on long-term availability.',
      message: `They shared a preference for high family involvement in their physical caregiving, which is keeping costs down by putting the labor on the family. It may be important to validate the feasibility of this in order to rely on it.`,
      sentiment: 'caution',
    };
  }

  if (isLow) {
    const title = familyPercent > 0
      ? `Family is expected to cover ${familyPercent}% of ${clientFirstName}'s caregiving needs.`
      : 'Care will be primarily provided by professional caregivers.';
    return {
      id: 'family-caregiving',
      category: 'care-structure',
      categoryLabel: 'Family Caregiving',
      locations: ['summary', 'care-phases', 'cost-factors'],
      title,
      titleHighlights: familyPercent > 0 ? [`${familyPercent}%`] : ['primarily'],
      description: 'Without significant family involvement, professional care costs make up a larger share of the projections.',
      summaryDescription: 'Professional care makes up more of your projected costs.',
      message: `They indicated minimal or no family involvement in physical caregiving, meaning most care will be provided by professional caregivers or at care facilities, which contributes to higher costs.`,
      sentiment: 'neutral',
    };
  }

  const title = familyPercent > 0
    ? `Family is expected to cover ${familyPercent}% of ${clientFirstName}'s caregiving needs.`
    : `Family caregiving will play a moderate role in ${clientFirstName}'s care plan.`;
  return {
    id: 'family-caregiving',
    category: 'care-structure',
    categoryLabel: 'Family Caregiving',
    locations: ['summary', 'care-phases', 'cost-factors'],
    title,
    titleHighlights: familyPercent > 0 ? [`${familyPercent}%`] : ['moderate role'],
    description: 'A mix of family and professional care is factored into the projections.',
    summaryDescription: 'A mix of family and professional care is factored in.',
    message: `They indicated a moderate level of family involvement in caregiving.`,
    sentiment: 'neutral',
  };
};

export const predictionInsightRules: InsightRule[] = [
  costOfLivingRule,
  inflationHorizonRule,
  likelihoodRule,
  familyCaregivingRule,
];
